import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Database, ExternalLink, CheckCircle, Calendar, BookOpen } from 'lucide-react';
import axios from 'axios';
import { API } from "@/config";

const SourcesPage = () => {
  const [sources, setSources] = useState([]);
  const [electionFacts, setElectionFacts] = useState({});
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const response = await axios.get(`${API}/sources`);
        setSources(response.data.sources || []);
        setElectionFacts(response.data.election_facts || {});
        setLastUpdated(response.data.last_updated || '');
      } catch (error) {
        console.error('Error fetching sources:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSources();
  }, []);

  const getReliabilityColor = (reliability) => {
    switch (reliability) {
      case 'authoritative':
        return 'text-green-400 bg-green-400/20';
      case 'high':
        return 'text-blue-400 bg-blue-400/20';
      case 'medium':
        return 'text-yellow-400 bg-yellow-400/20';
      default:
        return 'text-gray-400 bg-gray-400/20';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gorkhali-red via-black to-marigold flex items-center justify-center">
        <div className="text-white font-bebas text-2xl">Loading sources...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gorkhali-red via-black to-marigold pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Database className="text-marigold" size={48} />
            <h1 className="font-bebas text-6xl text-white tracking-wider">
              Data Sources
            </h1>
          </div>
          <p className="text-gray-300 text-lg">
            Transparency in Election Information
          </p>
        </motion.div>

        {/* Election Facts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 mb-8"
        >
          <h2 className="font-bebas text-3xl text-white tracking-wider mb-6 flex items-center gap-2">
            <CheckCircle className="text-marigold" size={32} />
            Verified Election Facts
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(electionFacts).map(([key, data]) => (
              <div key={key} className="bg-white/5 rounded-lg p-4">
                <div className="text-gray-400 text-sm mb-1 capitalize">
                  {key.replace(/_/g, ' ')}
                </div>
                <div className="text-white text-2xl font-bebas tracking-wider">
                  {typeof data.value === 'string' && data.value.match(/^\d{4}-\d{2}-\d{2}$/)
                    ? new Date(data.value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                    : data.value}
                </div>
                {data.verified && (
                  <div className="flex items-center gap-1 text-green-400 text-sm mt-1">
                    <CheckCircle size={14} />
                    Verified
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Primary Sources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="font-bebas text-3xl text-marigold tracking-wider mb-6">
            Primary Sources (Authoritative)
          </h2>
          <div className="space-y-4">
            {sources.filter(s => s.category === 'primary').map((source, index) => (
              <motion.div
                key={source.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6 hover:border-marigold/50 transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bebas text-2xl text-white tracking-wider">
                      {source.name}
                    </h3>
                    {source.name_ne && (
                      <p className="text-gray-400 text-sm">{source.name_ne}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getReliabilityColor(source.reliability)}`}>
                    {source.reliability}
                  </span>
                </div>
                <p className="text-gray-300 mb-3">{source.description}</p>
                <div className="flex items-center gap-4 text-sm">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-marigold hover:underline inline-flex items-center gap-1"
                  >
                    {source.url}
                    <ExternalLink size={14} />
                  </a>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-400">
                    {source.data_types.join(', ')}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Secondary Sources */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="font-bebas text-3xl text-marigold tracking-wider mb-6">
            Secondary Sources (Verification)
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {sources.filter(s => s.category === 'secondary').map((source, index) => (
              <motion.div
                key={source.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-5 hover:border-marigold/50 transition-all"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bebas text-xl text-white tracking-wider">
                    {source.name}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getReliabilityColor(source.reliability)}`}>
                    {source.reliability}
                  </span>
                </div>
                <p className="text-gray-300 text-sm mb-3">{source.description}</p>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-marigold hover:underline text-sm inline-flex items-center gap-1"
                >
                  Visit Source
                  <ExternalLink size={12} />
                </a>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Last Updated */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-lg border border-white/20 rounded-lg px-6 py-3">
            <Calendar className="text-marigold" size={20} />
            <span className="text-gray-300">
              Last Updated: {new Date(lastUpdated).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </motion.div>

        {/* Back to Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="text-center mt-6"
        >
          <a
            href="/disclaimer"
            className="inline-flex items-center gap-2 text-marigold hover:underline"
          >
            <BookOpen size={20} />
            View Full Disclaimer
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default SourcesPage;
